import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Brain, Zap, BookOpen, Lightbulb, Clock } from 'lucide-react';
export function HeroSection() {
  return <section className="relative min-h-screen flex items-center pt-20 pb-20 overflow-hidden" data-id="element-58">
      {/* Background Glows */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-cerebro-blue/20 rounded-full blur-[120px] -z-10" data-id="element-59" />
      <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-cerebro-orange/15 rounded-full blur-[120px] -z-10" data-id="element-60" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-61">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="max-w-2xl z-10 text-center lg:text-left pt-10 lg:pt-0" data-id="element-62">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-display font-bold leading-[1.1] mb-6 text-white" data-id="element-63">
            Domina tu mente y aprende{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cerebro-blue to-cyan-400" data-id="element-64">
              más rápido
            </span>{' '}
            que nunca
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 mb-10 leading-relaxed max-w-xl mx-auto lg:mx-0" data-id="element-65">
            Descubre el Método M3: Memoriza, organiza y retiene información sin
            esfuerzo.
          </p>
          <motion.a href="https://ideas-que-transforman-2.myshopify.com/products/metodo-m3-para-mejorar-la-memoria" target="_blank" rel="noopener noreferrer" whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white transition-all duration-300 bg-cerebro-orange rounded-full shadow-[0_0_20px_rgba(249,115,22,0.5)] hover:shadow-[0_0_35px_rgba(249,115,22,0.8)]" data-id="element-66">
            Quiero mejorar mi memoria ahora
          </motion.a>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 1,
        delay: 0.2
      }} className="relative h-[400px] sm:h-[500px] flex items-center justify-center" data-id="element-67">
          {/* Animated Brain Visual */}
          <div className="relative w-full max-w-md aspect-square" data-id="element-68">
            {/* Central Brain */}
            <motion.div animate={{
            y: [-10, 10, -10]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute inset-0 flex items-center justify-center z-10" data-id="element-69">
              <div className="relative" data-id="element-70">
                <Brain className="w-40 h-40 sm:w-56 sm:h-56 text-cerebro-blue drop-shadow-[0_0_30px_rgba(59,130,246,0.8)]" strokeWidth={1} data-id="element-71" />
                <div className="absolute inset-0 bg-cerebro-blue/20 blur-[60px] rounded-full" data-id="element-72" />
              </div>
            </motion.div>

            {/* Floating Elements */}
            <FloatingIcon Icon={Zap} className="top-4 left-4 sm:top-10 sm:left-10 text-yellow-400 shadow-[0_0_20px_rgba(250,204,21,0.3)]" delay={0} data-id="element-73" />
            <FloatingIcon Icon={BookOpen} className="bottom-10 left-0 sm:bottom-20 sm:left-4 text-cerebro-orange shadow-[0_0_20px_rgba(249,115,22,0.3)]" delay={1.5} data-id="element-74" />
            <FloatingIcon Icon={Lightbulb} className="top-10 right-0 sm:top-20 sm:right-10 text-cyan-400 shadow-[0_0_20px_rgba(34,211,238,0.3)]" delay={3} data-id="element-75" />
            <FloatingIcon Icon={Clock} className="bottom-4 right-4 sm:bottom-16 sm:right-16 text-purple-400 shadow-[0_0_20px_rgba(192,132,252,0.3)]" delay={4.5} data-id="element-76" />

            {/* Connecting SVG Lines */}
            <svg className="absolute inset-0 w-full h-full -z-10 opacity-40" viewBox="0 0 400 400" data-id="element-77">
              <motion.path d="M 200 200 L 80 80" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" className="text-yellow-400" initial={{
              pathLength: 0
            }} animate={{
              pathLength: 1
            }} transition={{
              duration: 2,
              repeat: Infinity
            }} data-id="element-78" />
              <motion.path d="M 200 200 L 80 320" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" className="text-cerebro-orange" initial={{
              pathLength: 0
            }} animate={{
              pathLength: 1
            }} transition={{
              duration: 2.5,
              repeat: Infinity
            }} data-id="element-79" />
              <motion.path d="M 200 200 L 320 120" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" className="text-cyan-400" initial={{
              pathLength: 0
            }} animate={{
              pathLength: 1
            }} transition={{
              duration: 3,
              repeat: Infinity
            }} data-id="element-80" />
              <motion.path d="M 200 200 L 320 320" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" className="text-purple-400" initial={{
              pathLength: 0
            }} animate={{
              pathLength: 1
            }} transition={{
              duration: 3.5,
              repeat: Infinity
            }} data-id="element-81" />
            </svg>
          </div>
        </motion.div>
      </div>
    </section>;
}
function FloatingIcon({
  Icon,
  className,
  delay
}: {
  Icon: any;
  className: string;
  delay: number;
}) {
  return <motion.div animate={{
    y: [-15, 15, -15],
    rotate: [-5, 5, -5]
  }} transition={{
    duration: 5,
    delay,
    repeat: Infinity,
    ease: 'easeInOut'
  }} className={`absolute p-3 sm:p-4 glass-card border-slate-700/50 ${className}`} data-id="element-82">
      <Icon className="w-6 h-6 sm:w-8 sm:h-8" data-id="element-83" />
    </motion.div>;
}